import React, { useState } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from '../../Providers/FloatMessage';
import { ResetParticipantBL } from '../../../utilities/BL/PersonalNoisemeter';

/**
 * Modal de Confirmación para Reactivar Participación (Reset) de un aficionado
 */
export const PersonalNoisemeterResetModal = ({
    isOpen,
    onClose,
    participant = null,
    enableScoreFormula = false,
    onResetSuccess = null
}) => {
    const { showMessage } = useFloatMessage();

    const REASON_PRESETS = [
        'Falla de conectividad',
        'Error de micrófono',
        'Recarga accidental',
        'Otro'
    ];

    const [selectedPreset, setSelectedPreset] = useState('Falla de conectividad');
    const [customReason, setCustomReason] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    if (!isOpen || !participant) return null;

    const handleConfirm = async (e) => {
        if (e) e.preventDefault();

        const reasonFinal = selectedPreset === 'Otro'
            ? customReason.trim()
            : (customReason.trim() ? `${selectedPreset} - ${customReason.trim()}` : selectedPreset);

        if (!reasonFinal || reasonFinal.length < 3) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Por favor ingresa un motivo válido para la reactivación (mínimo 3 caracteres).'
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                participantId: participant.id,
                reason: reasonFinal
            };

            const res = await ResetParticipantBL(payload);
            if (res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `¡Participante ${participant.fullName || participant.participationTicket} reactivado con éxito!`
                });
                if (onResetSuccess) onResetSuccess(participant.id);
                if (onClose) onClose();
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: res.message || 'No fue posible reactivar al participante.'
                });
            }
        } catch (err) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de red al intentar reactivar al participante.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {/* Backdrop */}
            <div
                onClick={onClose}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    backgroundColor: 'rgba(0, 0, 0, 0.75)',
                    zIndex: 99998,
                    backdropFilter: 'blur(4px)'
                }}
            />

            {/* Modal Box */}
            <div
                style={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '95%',
                    maxWidth: '560px',
                    zIndex: 99999
                }}
            >
                <div className="card shadow-lg border-0 rounded-4 overflow-hidden text-dark bg-white">
                    {/* Header */}
                    <div className="bg-warning text-dark p-3 px-4 d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-arrow-counterclockwise fs-4 fw-bold"></i>
                            <h5 className="mb-0 fw-bold">Reactivar Participante</h5>
                        </div>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={onClose}
                            disabled={isSubmitting}
                        ></button>
                    </div>

                    {/* Body */}
                    <div className="card-body p-4">
                        {/* Info del Participante */}
                        <div className="bg-light p-3 rounded-3 border mb-3">
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <h6 className="fw-bold mb-1 text-dark">{participant.fullName}</h6>
                                    <div className="text-secondary small">
                                        <i className="bi bi-envelope me-1"></i>{participant.email || 'Sin correo'}
                                        <span className="mx-2">•</span>
                                        <i className="bi bi-telephone me-1"></i>{participant.phone || 'Sin teléfono'}
                                    </div>
                                </div>
                                <span className="badge bg-dark fs-6 px-2 py-1 font-monospace">
                                    {participant.participationTicket}
                                </span>
                            </div>

                            {participant.status === 'COMPLETED' && (
                                <div className="mt-2 pt-2 border-top d-flex gap-3 text-muted small">
                                    <span>Pico: <strong>{participant.decibelsPeak} dB</strong></span>
                                    {enableScoreFormula ? (
                                        <span>Score: <strong>{participant.finalScore} pts</strong></span>
                                    ) : (
                                        <span>Promedio: <strong>{participant.decibelsAverage || 0} dB</strong></span>
                                    )}
                                    <span>Intento: <strong>#{participant.attemptNumber || 1}</strong></span>
                                </div>
                            )}
                        </div>

                        {/* Advertencia */}
                        <div className="alert alert-warning d-flex align-items-start gap-2 py-2 px-3 small rounded-3 mb-3">
                            <i className="bi bi-exclamation-triangle-fill fs-5 text-warning-emphasis flex-shrink-0 mt-0"></i>
                            <div>
                                <strong>Acción de Auditoría:</strong> El intento actual y puntuación de este participante serán archivados. El aficionado podrá volver a registrarse y gritar nuevamente con su mismo número o correo.
                            </div>
                        </div>

                        {/* Motivo de Reactivación */}
                        <div className="mb-3">
                            <label className="form-label fw-semibold small text-secondary mb-2">
                                Selecciona el motivo de la reactivación:
                            </label>
                            <div className="d-flex flex-wrap gap-2 mb-2">
                                {REASON_PRESETS.map((preset) => (
                                    <button
                                        key={preset}
                                        type="button"
                                        onClick={() => setSelectedPreset(preset)}
                                        className={`btn btn-sm rounded-pill fw-semibold px-3 ${
                                            selectedPreset === preset
                                                ? 'btn-warning text-dark border-warning'
                                                : 'btn-outline-secondary'
                                        }`}
                                    >
                                        {selectedPreset === preset && <i className="bi bi-check2 me-1"></i>}
                                        {preset}
                                    </button>
                                ))}
                            </div>

                            <label className="form-label fw-semibold small text-secondary mb-1">
                                {selectedPreset === 'Otro' ? 'Escribe el motivo detallado:' : 'Detalle o notas adicionales (opcional):'}
                            </label>
                            <textarea
                                className="form-control small"
                                rows="2"
                                placeholder="Ej. El participante experimentó microfonía saturada o recarga inesperada..."
                                value={customReason}
                                onChange={(e) => setCustomReason(e.target.value)}
                                required={selectedPreset === 'Otro'}
                            ></textarea>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="card-footer bg-light p-3 px-4 d-flex justify-content-end gap-2 border-top">
                        <button
                            type="button"
                            className="btn btn-outline-secondary px-4 fw-semibold"
                            onClick={onClose}
                            disabled={isSubmitting}
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className="btn btn-warning text-dark px-4 fw-bold d-flex align-items-center gap-2"
                            onClick={handleConfirm}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm" role="status"></span>
                                    <span>Reactivando...</span>
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-arrow-counterclockwise fs-5"></i>
                                    <span>Confirmar Reactivación</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PersonalNoisemeterResetModal;
